/**
 * Theme configuration objects for the EllaFin 6-theme switcher system.
 *
 * Each config defines the complete color palette and asset paths for a theme.
 * Bold themes use heavier font weights, larger headings, and deeper shadows.
 *
 * @module themeConfigs
 */

/**
 * Shared asset paths used by all themes.
 * @type {Object}
 */
const sharedAssets = {
  headerLogo: `${process.env.PUBLIC_URL}/logos/ellafin-logo.png`,
  footerLogo: `${process.env.PUBLIC_URL}/images/full-logo-white-green.png`,
  logoMark: `${process.env.PUBLIC_URL}/images/ella-fin-logo.jpg`,
  partnerLogo: `${process.env.PUBLIC_URL}/images/r-logo.png`,
  awsBadge: `${process.env.PUBLIC_URL}/images/aws-marketplace-badge.png`,
  awsQualifiedBadge: `${process.env.PUBLIC_URL}/images/badges/aws-qualified-software-badge.png`,
  awsXceleratorBadge: `${process.env.PUBLIC_URL}/images/badges/business outcomes xcelerator.png`,
  finOpsBadge: `${process.env.PUBLIC_URL}/images/badges/FinOps-Badge-GeneralMember.png`,
};

/**
 * @typedef {Object} ThemeConfig
 * @property {string} id - Unique identifier for the theme
 * @property {string} name - Human-readable display name
 * @property {boolean} bold - Whether this theme uses bold styling (heavier weights, larger headings)
 * @property {string} primary - Primary brand color
 * @property {string} accent - Accent color for CTAs and highlights
 * @property {string} cream - Cream/warm neutral background color
 * @property {string} heroBackground - Background color or gradient for hero sections
 * @property {string} headerBackground - Background color for the site header
 * @property {string} footerBackground - Background color for the site footer
 * @property {string} sectionAlt - Alternate section background color
 * @property {string} textOnHero - Text color used on hero sections
 * @property {string} textPrimary - Primary text color for body content
 * @property {string} textSecondary - Secondary/muted text color
 * @property {Object} assets - Asset file paths for logos and badges
 */

/** @type {ThemeConfig} */
export const deepPurple = {
  id: 'deep-purple',
  name: 'Deep Purple',
  bold: true,
  primary: '#585286',
  accent: '#779a0a',
  cream: '#f4f0eb',
  heroBackground: '#585286',
  headerBackground: '#585286',
  footerBackground: '#3d3860',
  sectionAlt: '#f4f0eb',
  textOnHero: '#ffffff',
  textPrimary: '#1a1a2e',
  textSecondary: '#4d4d6a',
  assets: { ...sharedAssets },
};

/** @type {ThemeConfig} */
export const mediumPurple = {
  id: 'medium-purple',
  name: 'Medium Purple',
  bold: true,
  primary: '#9a7cb2',
  accent: '#779a0a',
  cream: '#f4f0eb',
  heroBackground: '#9a7cb2',
  headerBackground: '#9a7cb2',
  footerBackground: '#7a5f94',
  sectionAlt: '#f4f0eb',
  textOnHero: '#ffffff',
  textPrimary: '#1a1a2e',
  textSecondary: '#4d4d6a',
  assets: { ...sharedAssets },
};

/** @type {ThemeConfig} */
export const greyBlue = {
  id: 'grey-blue',
  name: 'Grey Blue',
  bold: true,
  primary: '#90acad',
  accent: '#779a0a',
  cream: '#f4f0eb',
  heroBackground: '#90acad',
  headerBackground: '#90acad',
  footerBackground: '#6f8c8d',
  sectionAlt: '#f4f0eb',
  textOnHero: '#ffffff',
  textPrimary: '#1a1a2e',
  textSecondary: '#4d4d6a',
  assets: { ...sharedAssets },
};

/** @type {ThemeConfig} */
export const purpleGradient = {
  id: 'purple-gradient',
  name: 'Purple Gradient',
  bold: true,
  primary: '#585286',
  accent: '#779a0a',
  cream: '#f4f0eb',
  heroBackground: '#585286',
  headerBackground: '#585286',
  footerBackground: '#3d3860',
  sectionAlt: '#f4f0eb',
  textOnHero: '#ffffff',
  textPrimary: '#1a1a2e',
  textSecondary: '#4d4d6a',
  assets: { ...sharedAssets },
};

/** @type {ThemeConfig} */
export const midnightNavy = {
  id: 'midnight-navy',
  name: 'Midnight Navy',
  bold: true,
  primary: '#1e2140',
  accent: '#779a0a',
  cream: '#f4f0eb',
  heroBackground: '#1e2140',
  headerBackground: '#1e2140',
  footerBackground: '#13152b',
  sectionAlt: '#f4f0eb',
  textOnHero: '#ffffff',
  textPrimary: '#1a1a2e',
  textSecondary: '#4d4d6a',
  assets: {
    ...sharedAssets,
    headerLogo: `${process.env.PUBLIC_URL}/images/full-logo-white-green.png`,
  },
};

/** @type {ThemeConfig} */
export const original = {
  id: 'original',
  name: 'Original',
  bold: false,
  primary: '#5e4d89',
  accent: '#7e9419',
  cream: '#f4f0eb',
  heroBackground: '#ffffff',
  headerBackground: '#ffffff',
  footerBackground: '#3d3160',
  sectionAlt: '#f7f6f9',
  textOnHero: '#0f1729',
  textPrimary: '#0f1729',
  textSecondary: '#4d6280',
  assets: { ...sharedAssets },
};

/**
 * Array of all theme configurations, ordered for display in the selector.
 * @type {ThemeConfig[]}
 */
export const allThemeConfigs = [
  deepPurple,
  mediumPurple,
  greyBlue,
  purpleGradient,
  midnightNavy,
  original,
];
